import React, { useEffect, useState, useRef } from "react";
import { api, resolveImage } from "../lib/api";
import { ChevronLeft, ChevronRight, HeartHandshake } from "lucide-react";

// Continuous-scroll speed in pixels per frame (~60 fps).
// 0.5 → very gentle, 1.0 → noticeable, 1.5 → brisk.
const SCROLL_SPEED_PX = 0.6;

export const DonationsCarousel = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef(null);
  const pausedRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    api
      .get("/donations")
      .then((res) => {
        if (mounted) setPhotos(res.data || []);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  // Render the photos list twice so we can wrap seamlessly when scrollLeft
  // crosses the halfway point (end of the first copy).
  const looped = photos.length > 1 ? [...photos, ...photos] : photos;

  const scrollByOne = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const step = card ? card.offsetWidth + 16 : 320;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  // Continuous auto-scroll using rAF — smooth, slow drift with seamless loop.
  useEffect(() => {
    if (photos.length < 2) return undefined;
    const tick = () => {
      const el = trackRef.current;
      if (el && !pausedRef.current) {
        const halfWidth = el.scrollWidth / 2;
        let next = el.scrollLeft + SCROLL_SPEED_PX;
        if (next >= halfWidth) {
          // Jump back instantly by halfWidth — invisible because the second
          // copy looks identical to the first at that point.
          next -= halfWidth;
          el.scrollLeft = next;
        } else {
          el.scrollLeft = next;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [photos.length]);

  const handlePause = () => {
    pausedRef.current = true;
  };
  const handleResume = () => {
    pausedRef.current = false;
  };

  if (loading || photos.length === 0) return null;

  return (
    <section
      id="donations-past"
      className="w-full py-16 md:py-24 px-6"
      style={{ backgroundColor: "var(--bg-secondary)" }}
      data-testid="donations-carousel-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <div
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-3"
            style={{ backgroundColor: "var(--brand-peach)", color: "var(--text-accent)" }}
          >
            <HeartHandshake size={14} />
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold">
              donations of the past
            </span>
          </div>
          <h2
            className="font-display text-4xl md:text-5xl lg:text-6xl mb-3"
            style={{ color: "var(--text-primary)" }}
          >
            Smiles We’ve Stitched
          </h2>
          <p className="text-base md:text-lg max-w-xl mx-auto" style={{ color: "var(--text-secondary)" }}>
            A look back at hospital visits and giving moments — every plushie you
            purchase helps create more of these.
          </p>
        </div>

        <div className="relative">
          {photos.length > 1 && (
            <>
              <button
                onClick={() => scrollByOne(-1)}
                className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-11 h-11 rounded-full bg-white shadow-lg"
                style={{ border: "1px solid var(--border-soft)", color: "var(--brand-sage)" }}
                aria-label="Previous photo"
                data-testid="donations-prev-button"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => scrollByOne(1)}
                className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-11 h-11 rounded-full bg-white shadow-lg"
                style={{ border: "1px solid var(--border-soft)", color: "var(--brand-sage)" }}
                aria-label="Next photo"
                data-testid="donations-next-button"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}

          <div
            ref={trackRef}
            className="flex gap-4 overflow-x-auto pb-4"
            style={{ scrollbarWidth: "thin" }}
            onMouseEnter={handlePause}
            onMouseLeave={handleResume}
            onTouchStart={handlePause}
            onTouchEnd={handleResume}
            data-testid="donations-track"
          >
            {looped.map((p, idx) => (
              <figure
                key={`${p.id}-${idx}`}
                data-card
                className="flex-shrink-0 w-[78%] sm:w-[48%] lg:w-[31%] rounded-2xl overflow-hidden bg-white"
                style={{ border: "1px solid var(--border-soft)" }}
                data-testid={idx < photos.length ? `donation-card-${p.id}` : undefined}
              >
                <div className="aspect-[4/3] bg-[var(--bg-tertiary)]">
                  <img
                    src={resolveImage(p.image_url)}
                    alt={p.caption || "Donation moment"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    draggable={false}
                  />
                </div>
                {p.caption && (
                  <figcaption
                    className="px-4 py-3 text-sm leading-relaxed"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {p.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DonationsCarousel;
